import * as _ from 'lodash';
import { CurrentSchoolYear } from 'Src/ng2/shared/constants/current-school-year.constant';
import { ImUser } from 'Src/ng2/shared/services/im-models/im-user';
import { IUser } from 'Src/ng2/shared/typings/interfaces/user.interface';
import { RegentsSubject } from '../../../../ng2/shared/constants/regents.constant';
import { SupportCategories } from '../../../../ng2/shared/constants/support-categories.constant';
import { SupportStatuses } from '../../../../ng2/shared/constants/support-statuses.constant';
import nvSharedModelsModule from '../nv.shared.models.module';

const DEFAULT_SUPPORT = SupportCategories.REGENTS_PREP.key;

angular
  .module(nvSharedModelsModule)
  .factory('Support', function(
    NvBaseModel,
    SupportScheduleService,
    UtilitiesService,
    ImSupport,
    enqueuedPatchFieldsMixin,
    ImUser: ImUser,
  ) {
    /**
     * The `metaData` property on `SUPPORT_TEMPLATE` formerly called a constant, `_getMetaData`, which was
     * attached to the `Support` class so that a Support instance method, `setMetaData` could also use it.
     *
     * Since the prototype methods are being moved to `ImSupport`, I also moved `_getMetaData` to `ImSupport`
     * as well and made it a public method. Since it lives there now, both the template here and the
     * current `setMetaData` prototype methods here have been updated to call `ImSupport.getMetaData`.
     *
     * When all app code is refactored to use ImSupport, remove the redundant `setMetaData`
     * method from the `Support` prototype.
     *
     * - JYR
     */
    const SUPPORT_TEMPLATE = {
      _id: null,
      schoolId: null,
      category: DEFAULT_SUPPORT,
      name: null,
      description: null,
      location: null,
      schedule: {
        repeats: 'NONE',
        repeatsEvery: 1,
        repeatsBy: 'NONE',
        repeatsOn: {
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
          sunday: false,
        },
        startsOn: null,
        endsOn: null,
        startTime: null,
        endTime: null,
      },
      activityLead: {
        userId: null,
        firstName: null,
        lastName: null,
        gafeEmail: null,
        doeEmail: null,
      },
      primaryOutcomeMetrics: [],
      metaData: ImSupport.getMetaData(DEFAULT_SUPPORT),
    };

    const PERMISSIONING_MODEL_NAME = 'SUPPORT';
    const relationToUserPath = 'createdBy.userId';

    let Support = NvBaseModel(
      // eslint-disable-line new-cap
      '/v1/supports/:id',
      {},
      {
        query: {
          params: {
            schoolId: '@schoolId',
          },
        },
        _customQuery: {
          method: 'GET',
          isArray: true,
          transformResponse(data) {
            let json = angular.fromJson(data);
            // if the request is cancelled, json will be null
            if (json) return json.data;
          },
          params: {
            where: '@where',
          },
        },
      },
      null,
      PERMISSIONING_MODEL_NAME,
      relationToUserPath,
    );

    /**
     * NOTE: prototype method definitions are now in ImSupport.
     * TODO: Remove prototype methods here once related app code is refactored to call ImSupport
     *
     * - JYR
     */
    Support.extend({
      // TODO this belongs somewhere else
      _getExamSubjectHumanName(examSubject) {
        return _.find(RegentsSubject, { key: examSubject }).humanName;
      },

      getStartAndEndDateString(schedule) {
        const self = this;
        schedule = schedule || self.schedule;
        const startHuman = UtilitiesService.convertISODateToHumanDate(schedule.startsOn);
        const endHuman = UtilitiesService.convertISODateToHumanDate(schedule.endsOn);
        let str = startHuman;
        if (endHuman) str += ` - ${endHuman}`;
        return str;
      },

      getStartAndEndTimeString(schedule) {
        const self = this;
        schedule = schedule || self.schedule;
        return `${schedule.startTime} - ${schedule.endTime}`;
      },

      getMeetsOnScheduleString(schedule) {
        const self = this;
        schedule = schedule || self.schedule;
        let result = '';

        if (schedule.repeatsEvery === 1) {
          if (schedule.repeats === 'WEEKLY') {
            result += 'Weekly ';
          } else if (schedule.repeats === 'DAILY') {
            result += 'Daily ';
          } else if (schedule.repeats === 'MONTHLY') {
            result += 'Monthly ';
          }
        }

        if (schedule.repeatsEvery > 1) {
          result += `Every ${schedule.repeatsEvery} `;
          if (schedule.repeats === 'WEEKLY') {
            result += 'weeks ';
          } else if (schedule.repeats === 'DAILY') {
            result += 'days ';
          } else if (schedule.repeats === 'MONTHLY') {
            result += 'months ';
          }
        }

        if (schedule.repeats === 'WEEKLY') {
          const repeatsOnString = UtilitiesService.getWeeklyScheduleString(schedule.repeatsOn);
          result += `on ${repeatsOnString}`;
        }
        return result;
      },

      getLeadHumanName() {
        const self = this;
        const activityLead = self.activityLead;
        return `${activityLead.firstName} ${activityLead.lastName}`;
      },

      getCreatorHumanName() {
        const self = this;
        const creator = self.createdBy;
        return `${creator.firstName} ${creator.lastName}`;
      },

      categoryIsRegentsPrep(category) {
        const self = this;
        category = category || self.category;
        const catIsRegentsPrep = category === SupportCategories.REGENTS_PREP.key ? true : false;
        return catIsRegentsPrep;
      },

      getCategoryHumanName(category, metaData) {
        const self = this;
        category = category || self.category;
        metaData = metaData || self.metaData;
        const categoryDetails = SupportCategories[category];
        if (!categoryDetails) return '';

        let human = `${categoryDetails.humanName}`;
        if (self.categoryIsRegentsPrep(category)) {
          const humanSubj = self._getExamSubjectHumanName(metaData.examSubject);
          human = `${human} ${humanSubj}`;
        }
        return human;
      },

      getFrontendStatus() {
        return SupportScheduleService.getSupportStatusBasedOnSchedule(this);
      },

      getSortOrderBasedOnFrontendStatus() {
        return SupportScheduleService.getSupportOrderBasedOnCurrentState(this);
      },

      isDeleted() {
        return this.status === SupportStatuses.backend.DELETED;
      },

      isInProgress() {
        const frontEndStatus = this.getFrontendStatus();
        return frontEndStatus === SupportStatuses.frontend.IN_PROGRESS;
      },

      isNotStarted() {
        const frontEndStatus = this.getFrontendStatus();
        return frontEndStatus === SupportStatuses.frontend.NOT_STARTED;
      },

      isCompleted() {
        const frontEndStatus = this.getFrontendStatus();
        return frontEndStatus === SupportStatuses.frontend.COMPLETED;
      },

      // Sets the appropriate primaryOutcomeMetrics for the Support instance
      setPrimaryOutcomeMetrics() {
        const cat = this.category;
        const supportConst = SupportCategories[cat];
        const metrics = supportConst.primaryOutcomeMetrics;

        // For Regents Prep supports, filter the possible metrics for the given Regents subject
        // E.g. if the support is for alg, return 'regentsDetails.byCategory9.alg.maxScore', etc.
        // For all other types, just return the full array of metrics
        if (cat === SupportCategories.REGENTS_PREP.key) {
          const { examSubject } = this.metaData;
          this.primaryOutcomeMetrics = _.filter(metrics, (metricPath: any) => {
            const metricSubject = metricPath.split('.')[2];
            return metricSubject === examSubject;
          });
        } else {
          this.primaryOutcomeMetrics = metrics;
        }
      },

      setMetaData({
        category,
        examSubject = null,
        serviceName = null,
        subject = null,
      }: {
        category;
        examSubject?;
        serviceName?;
        subject?;
      }): void {
        this.metaData = ImSupport.getMetaData(category);
        if (examSubject) this.metaData.examSubject = examSubject;
        if (serviceName) this.metaData.serviceName = serviceName;
        if (subject) this.metaData.subject = subject;
      },
    });

    Support.placePublicImSupportStaticMethodsOnPrototype = function() {
      const ImSupportStaticMethods = _.difference(Object.getOwnPropertyNames(ImSupport), [
        'length',
        'name',
        'prototype',
      ]);

      // remove ImSupport private methods
      _.remove(ImSupportStaticMethods, staticMethod => {
        return staticMethod.indexOf('_') === 0;
      });
      /**
       * NOTE: This guards against methods being added to ImSupport that already exist in Support.
       * However, there will be a short amount of time where there will be app code using ImSupport
       * and other code that will need to continue to call prototype methods here until it can be refactored.
       * So this check is disabled until all app code can be refactored to use ImSupport - JYR
       */
      // _.forEach(ImSupportStaticMethods, (staticMethod) => {

      //   if (Support.prototype[staticMethod]) {
      //     throw new Error(`ImSupport.${staticMethod} static method already exists in Support.prototype`);
      //   }

      //   Support.prototype[staticMethod] = function (...params) {
      //     params.unshift(this);
      //     return ImSupport[staticMethod].apply(null, params);
      //   };
      // });
    };

    /**
     * Query for supports for a specific activityLead
     */
    Support.queryByActivityLead = function(userId) {
      return Support._customQuery({
        where: JSON.stringify({ 'activityLead.userId': userId }),
      });
    };

    /**
     * Contextually query for activityLead or schoolId, depending on user's:
     *  1. nvRole.type, 2. delegatedRole and 3. recalculatedRoleType given a contextual school Id
     */
    Support.queryByUserRoleForActiveSupports = function(user: IUser, schoolId: string) {
      const viewRoleType = ImUser.isViewType(user);
      const caseloadRoleType = ImUser.isCaseloadUser(user);
      const status = 'ACTIVE';

      // view or caseload level users can only see the activities they lead
      if (viewRoleType || caseloadRoleType) return Support.queryByActivityLead(user._id);

      // All other users can see school-wide supports
      return Support._customQuery({
        where: JSON.stringify({ schoolId, status }),
      });
    };

    Support.queryByUserRoleForThisSYSupports = function(user: IUser, schoolId: string) {
      const viewRoleType = ImUser.isViewType(user);
      const caseloadRoleType = ImUser.isCaseloadUser(user);
      const schoolYear = CurrentSchoolYear.WITH_SY_PREFIX;

      // view or caseload level users can only see the activities they lead
      if (viewRoleType || caseloadRoleType) return Support.queryByActivityLead(user._id);

      // All other users can see school-wide supports
      return Support._customQuery({
        where: JSON.stringify({ schoolId, schoolYear }),
      });
    };

    Support.getNotDeletedSupports = function(supports) {
      const activeSupports = _.filter(supports, (support: any) => {
        // return !support.isDeleted();
        return !ImSupport.isDeleted(support);
      });
      return _.sortBy(activeSupports, support => support.name.toLowerCase(), ['name']);
    };

    Support.getInProgressAndNotStartedSupports = function(supports) {
      return _.reduce(
        supports,
        (result, support: any) => {
          // if (support.isInProgress() || support.isNotStarted()) result.push(support);
          const isInProgress = ImSupport.isInProgress(support);
          const isNotStarted = ImSupport.isNotStarted(support);
          if (isInProgress || isNotStarted) result.push(support);
          return result;
        },
        [],
      );
    };

    Support.createFromTemplate = function() {
      const copy = angular.copy(SUPPORT_TEMPLATE);
      return new Support(copy);
    };

    Support.placePublicImSupportStaticMethodsOnPrototype();

    /**
     *  In the Student/ImStudent and School/ImSchool pattern, each service returns the following:
     *  `return enqueuedPatchFieldsMixin(Support)`
     *  I'm assuming this will also need to be the case for the Support/ImSupport pattern.
     *  For now, disabling call to that service until all other app code that relies on the
     *  current prototype methods found here can be refactored. - JYR
     */
    // return enqueuedPatchFieldsMixin(Support) // attaches specific methods to model before return
    return Support;
  });
